import type { ZodType } from 'zod';
import {
  ApiErrorSchema,
  AppConfigResponseSchema,
  CategoryListResponseSchema,
  CategorySchema,
  DiscountRuleListResponseSchema,
  DiscountRuleSchema,
  MeResponseSchema,
  UserHandoverListResponseSchema,
  OkResponseSchema,
  PreviewSaleResponseSchema,
  ProductListResponseSchema,
  ProductSchema,
  PublicUserSchema,
  SaleDetailSchema,
  SaleListResponseSchema,
  StockMovementListResponseSchema,
  TodayBreakdownResponseSchema,
  TodayReportSchema,
  UserListResponseSchema,
  type AdjustStockRequest,
  type AppConfigResponse,
  type Category,
  type CategoryListResponse,
  type ChangePasswordRequest,
  type CreateCategoryRequest,
  type CreateDiscountRuleRequest,
  type CreateProductRequest,
  type CreateSaleRequest,
  type CreateUserRequest,
  type DiscountRule,
  type DiscountRuleListResponse,
  type ErrorCode,
  type HandoverUserRequest,
  type ImageCrop,
  type LoginRequest,
  type MeResponse,
  type OkResponse,
  type PaymentMethod,
  type PreviewSaleRequest,
  type PreviewSaleResponse,
  type Product,
  type ProductListResponse,
  type PublicUser,
  type ReorderDiscountRulesRequest,
  type SaleDetail,
  type SaleListResponse,
  type StockMovementListResponse,
  type TodayBreakdownGroupBy,
  type TodayBreakdownResponse,
  type TodayReport,
  type UpdateCategoryRequest,
  type UpdateDiscountRuleRequest,
  type UpdateProductRequest,
  type UpdateUserRequest,
  type UserHandoverListResponse,
  type UserListResponse,
} from '@bazaar/shared';

/**
 * Called once per request when the server reports a known-expired session
 * (`SESSION_EXPIRED` - see `api/src/middleware/require-auth.ts`), as
 * opposed to simply never having been logged in. The default redirects to
 * the login page with `?expired=1`, which `LoginPage` reads to show an
 * explanatory note instead of a bare error - this is the app's "global
 * 401 handling" for an idle-timed-out session. Overridable (tests only;
 * nothing in the app itself needs to replace it) so it can be observed
 * without a real browser navigation.
 */
let sessionExpiredHandler = (): void => {
  if (typeof window !== 'undefined') {
    window.location.assign('/login?expired=1');
  }
};

export function setSessionExpiredHandler(handler: () => void): void {
  sessionExpiredHandler = handler;
}

/** Thrown by every `api.*` call on a non-2xx response, or a network/transport failure. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly details?: Record<string, unknown>;

  constructor(status: number, code: ErrorCode, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const WRITE_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

interface RequestOptions extends Omit<RequestInit, 'headers'> {
  schema?: ZodType<unknown>;
  headers?: Record<string, string>;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { schema, ...rest } = options;
  const method = (rest.method ?? 'GET').toUpperCase();
  const headers = new Headers(rest.headers);
  // The API requires Content-Type: application/json on every write request
  // (even ones with no body, e.g. logout) as part of its CSRF defence.
  // FormData (image upload) sets its own multipart boundary; forcing JSON here would break it.
  const isFormData = typeof FormData !== 'undefined' && rest.body instanceof FormData;
  if (WRITE_METHODS.has(method) && !headers.has('Content-Type') && !isFormData) {
    headers.set('Content-Type', 'application/json');
  }

  let res: Response;
  try {
    res = await fetch(path, { credentials: 'include', ...rest, headers });
  } catch {
    throw new ApiError(0, 'INTERNAL', 'Network error. Check your connection and try again.');
  }

  const text = await res.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!res.ok) {
    const parsed = ApiErrorSchema.safeParse(payload);
    if (parsed.success) {
      if (res.status === 401 && parsed.data.error.code === 'SESSION_EXPIRED') {
        sessionExpiredHandler();
      }
      throw new ApiError(
        res.status,
        parsed.data.error.code,
        parsed.data.error.message,
        parsed.data.error.details,
      );
    }
    throw new ApiError(res.status, 'INTERNAL', `Request failed with status ${res.status}`);
  }

  return (schema ? schema.parse(payload) : payload) as T;
}

function toQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === false) continue;
    search.set(key, value === true ? '1' : String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const api = {
  login: (body: LoginRequest) =>
    request<MeResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
      schema: MeResponseSchema,
    }),

  logout: () =>
    request<OkResponse>('/api/auth/logout', { method: 'POST', schema: OkResponseSchema }),

  me: () => request<MeResponse>('/api/auth/me', { schema: MeResponseSchema }),

  listCategories: () =>
    request<CategoryListResponse>('/api/categories', { schema: CategoryListResponseSchema }),

  listProducts: (params: { categoryId?: number; q?: string; includeInactive?: boolean } = {}) =>
    request<ProductListResponse>(`/api/products${toQueryString(params)}`, {
      schema: ProductListResponseSchema,
    }),

  /** Looks up one product by its stock code, e.g. from a scanned QR payload. Throws (404) when not found. */
  getProductByCode: (code: string) =>
    request<Product>(`/api/products/by-code/${encodeURIComponent(code)}`, {
      schema: ProductSchema,
    }),

  createSale: (body: CreateSaleRequest) =>
    request<SaleDetail>('/api/sales', {
      method: 'POST',
      body: JSON.stringify(body),
      schema: SaleDetailSchema,
    }),

  previewSale: (body: PreviewSaleRequest) =>
    request<PreviewSaleResponse>('/api/sales/preview', {
      method: 'POST',
      body: JSON.stringify(body),
      schema: PreviewSaleResponseSchema,
    }),

  listSalesToday: (params: { limit?: number; offset?: number } = {}) =>
    request<SaleListResponse>(`/api/sales${toQueryString(params)}`, {
      schema: SaleListResponseSchema,
    }),

  getSale: (id: number) => request<SaleDetail>(`/api/sales/${id}`, { schema: SaleDetailSchema }),

  voidSale: (id: number) =>
    request<SaleDetail>(`/api/sales/${id}/void`, { method: 'POST', schema: SaleDetailSchema }),

  getTodayReport: (params: { date?: string } = {}) =>
    request<TodayReport>(`/api/reports/today${toQueryString(params)}`, {
      schema: TodayReportSchema,
    }),

  getTodayBreakdown: (params: { groupBy?: TodayBreakdownGroupBy; date?: string } = {}) =>
    request<TodayBreakdownResponse>(`/api/reports/today/breakdown${toQueryString(params)}`, {
      schema: TodayBreakdownResponseSchema,
    }),

  listSales: (
    params: {
      from?: string;
      to?: string;
      userId?: number;
      paymentMethod?: PaymentMethod;
      limit?: number;
      offset?: number;
    } = {},
  ) =>
    request<SaleListResponse>(`/api/sales${toQueryString(params)}`, {
      schema: SaleListResponseSchema,
    }),

  // --- Categories (admin) ---
  createCategory: (body: CreateCategoryRequest) =>
    request<Category>('/api/categories', {
      method: 'POST',
      body: JSON.stringify(body),
      schema: CategorySchema,
    }),

  updateCategory: (id: number, body: UpdateCategoryRequest) =>
    request<Category>(`/api/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      schema: CategorySchema,
    }),

  deleteCategory: (id: number, params: { reassignTo?: number } = {}) =>
    request<OkResponse>(`/api/categories/${id}${toQueryString(params)}`, {
      method: 'DELETE',
      schema: OkResponseSchema,
    }),

  // --- Products (admin write side) ---
  createProduct: (body: CreateProductRequest) =>
    request<Product>('/api/products', {
      method: 'POST',
      body: JSON.stringify(body),
      schema: ProductSchema,
    }),

  updateProduct: (id: number, body: UpdateProductRequest) =>
    request<Product>(`/api/products/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      schema: ProductSchema,
    }),

  adjustStock: (id: number, body: AdjustStockRequest) =>
    request<Product>(`/api/products/${id}/stock`, {
      method: 'POST',
      body: JSON.stringify(body),
      schema: ProductSchema,
    }),

  uploadProductImage: (id: number, file: File, crop?: ImageCrop) => {
    const form = new FormData();
    form.set('image', file);
    if (crop) form.set('crop', JSON.stringify(crop));
    return request<Product>(`/api/products/${id}/image`, {
      method: 'POST',
      body: form,
      schema: ProductSchema,
    });
  },

  updateProductImageCrop: (id: number, crop: ImageCrop) =>
    request<Product>(`/api/products/${id}/image/crop`, {
      method: 'PATCH',
      body: JSON.stringify({ crop }),
      schema: ProductSchema,
    }),

  deleteProductImage: (id: number) =>
    request<Product>(`/api/products/${id}/image`, { method: 'DELETE', schema: ProductSchema }),

  // --- Discount rules (admin) ---
  listDiscountRules: () =>
    request<DiscountRuleListResponse>('/api/discount-rules', { schema: DiscountRuleListResponseSchema }),

  createDiscountRule: (body: CreateDiscountRuleRequest) =>
    request<DiscountRule>('/api/discount-rules', {
      method: 'POST',
      body: JSON.stringify(body),
      schema: DiscountRuleSchema,
    }),

  updateDiscountRule: (id: number, body: UpdateDiscountRuleRequest) =>
    request<DiscountRule>(`/api/discount-rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      schema: DiscountRuleSchema,
    }),

  deleteDiscountRule: (id: number) =>
    request<OkResponse>(`/api/discount-rules/${id}`, { method: 'DELETE', schema: OkResponseSchema }),

  reorderDiscountRules: (body: ReorderDiscountRulesRequest) =>
    request<DiscountRuleListResponse>('/api/discount-rules/reorder', {
      method: 'POST',
      body: JSON.stringify(body),
      schema: DiscountRuleListResponseSchema,
    }),

  // --- Stock movements (admin) ---
  listStockMovements: (
    params: {
      productId?: number;
      from?: string;
      to?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) =>
    request<StockMovementListResponse>(`/api/stock-movements${toQueryString(params)}`, {
      schema: StockMovementListResponseSchema,
    }),

  // --- Users (admin) ---
  listUsers: () => request<UserListResponse>('/api/users', { schema: UserListResponseSchema }),

  createUser: (body: CreateUserRequest) =>
    request<PublicUser>('/api/users', {
      method: 'POST',
      body: JSON.stringify(body),
      schema: PublicUserSchema,
    }),

  updateUser: (id: number, body: UpdateUserRequest) =>
    request<PublicUser>(`/api/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      schema: PublicUserSchema,
    }),

  handoverUser: (id: number, body: HandoverUserRequest) =>
    request<PublicUser>(`/api/users/${id}/handover`, {
      method: 'POST',
      body: JSON.stringify(body),
      schema: PublicUserSchema,
    }),

  listUserHandovers: (id: number) =>
    request<UserHandoverListResponse>(`/api/users/${id}/handovers`, {
      schema: UserHandoverListResponseSchema,
    }),

  changePassword: (body: ChangePasswordRequest) =>
    request<OkResponse>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(body),
      schema: OkResponseSchema,
    }),

  health: () => request<{ ok: boolean; time: string }>('/api/health'),

  // --- Training environment ---
  getConfig: () => request<AppConfigResponse>('/api/config', { schema: AppConfigResponseSchema }),

  trainingReset: (password: string) =>
    request<OkResponse>('/api/training/reset', {
      method: 'POST',
      body: JSON.stringify({ confirm: 'RESET', password }),
      schema: OkResponseSchema,
    }),

  trainingResetStock: (password: string) =>
    request<OkResponse>('/api/training/reset-stock', {
      method: 'POST',
      body: JSON.stringify({ confirm: 'RESET', password }),
      schema: OkResponseSchema,
    }),
};
